/**
 * Pedagogical Guard — Runtime question validation before rendering.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §5 — Runs between adaptive engine output
 * and PracticeSession render. Rejects questions that violate the
 * pedagogical contract, replacing them with alternatives when possible.
 *
 * Design principles:
 * - Transparent to the learner (rejected questions are silently replaced)
 * - No flow interruption (session always has the expected question count)
 * - Telemetry logging for governance review
 * - Fail-safe: if no replacement found, soft-fail questions still pass through
 */

import type { Question } from '../../content'
import { debug, warn } from '../../utils/logger'

// ─── Guard Verdict ────────────────────────────────────────────────────────────

export interface GuardVerdict {
  /** Whether the question passes the pedagogical guard */
  ok: boolean
  /** Machine-readable reason for rejection */
  reason?: string
  /** Severity: fatal = must replace, soft = allow but flag */
  severity?: 'fatal' | 'soft'
}

// ─── Tier-band helpers ────────────────────────────────────────────────────────

function getTierBand(tier: number): string {
  if (tier <= 4) return 'basic'
  if (tier <= 8) return 'intermediate'
  if (tier <= 12) return 'advanced'
  return 'expert'
}

const MIN_DOK_FOR_BAND: Record<string, number> = {
  basic: 1,
  intermediate: 1,
  advanced: 2,
  expert: 3,
}

// ─── Core Evaluation ──────────────────────────────────────────────────────────

/**
 * Evaluate a single question against the pedagogical contract.
 *
 * Checks:
 * 1. Tier-DOK mismatch (expert tier with DOK 1 = fatal)
 * 2. Indicator-answer parity (visual leaks answer = fatal)
 * 3. MC distractors untagged at tier ≥ 9 (soft)
 */
export function evaluateQuestion(q: Question): GuardVerdict {
  const tier = q.tier ?? 1

  // 1. Cognitive mismatch: high tier with low DOK
  if (q.pedagogy?.dok !== undefined) {
    const band = getTierBand(tier)
    const minDok = MIN_DOK_FOR_BAND[band] ?? 1
    if (q.pedagogy.dok < minDok) {
      return { ok: false, reason: 'tier-dok-mismatch', severity: 'fatal' }
    }
  }

  // 2. Indicator parity: visual indicator leaks the answer
  const mode = q.pedagogy?.mode ?? 'practice'
  if (mode !== 'instruction') {
    const tolerance = q.pedagogy?.indicatorParityTolerance ?? 0.05
    const allAids = [...(q.stimulusAids ?? []), ...(q.supportAids ?? [])]
    for (const aid of allAids) {
      const indicatorValue = (aid as any).indicatorValue
      if (indicatorValue === undefined) continue

      // Try to extract numeric answer
      const correctAnswer = q.correctAnswer
      let correctNum: number | null = null
      if (typeof correctAnswer === 'number') {
        // correctAnswer could be a direct numeric value OR an option index.
        // First try as option index (MC questions store the index of the correct option).
        if (q.options && correctAnswer < q.options.length) {
          const optText = q.options[correctAnswer]
          if (optText) {
            const parsed = parseFloat(optText.replace(',', '.'))
            if (!isNaN(parsed)) correctNum = parsed
          }
        }
        // If not resolved via options, treat as direct numeric value
        if (correctNum === null) {
          correctNum = correctAnswer
        }
      } else if (typeof correctAnswer === 'string') {
        const parsed = parseFloat(correctAnswer.replace(',', '.'))
        if (!isNaN(parsed)) correctNum = parsed
      }

      if (correctNum !== null) {
        const range = Math.abs(correctNum) || 1
        const distance = Math.abs(Number(indicatorValue) - correctNum) / range
        if (distance < tolerance) {
          return { ok: false, reason: 'indicator-trivial', severity: 'fatal' }
        }
      }
    }
  }

  // 3. MC + untagged misconceptions at tier ≥ 9 (soft — allow but flag)
  if (q.questionType === 'multiple_choice' && tier >= 9) {
    const tags = q.pedagogy?.misconceptionTags ?? []
    const wrongCount = (q.options?.length ?? 0) - 1
    if (wrongCount > 0 && tags.length === 0) {
      return { ok: false, reason: 'distractors-untagged', severity: 'soft' }
    }
  }

  return { ok: true }
}

// ─── Session Filter ───────────────────────────────────────────────────────────

export interface GuardRejection {
  /** ID of the rejected question */
  questionId: string
  /** Family the question belongs to */
  familyId?: string
  /** Machine-readable rejection reason */
  reason: string
  /** Severity: fatal = replaced/removed, soft = allowed through */
  severity: 'fatal' | 'soft'
}

export interface GuardStats {
  total: number
  passed: number
  softFailed: number
  fatalRejected: number
  replaced: number
  reasons: Record<string, number>
  /** P-9: Per-question rejection details for governance feedback */
  rejections: GuardRejection[]
}

/**
 * Filter a session through the pedagogical guard.
 *
 * - Fatal rejections are removed (replaced if possible)
 * - Soft failures pass through but are logged
 * - Returns filtered questions and stats for telemetry
 *
 * @param questions - Raw session from adaptive engine
 * @param targetCount - Desired session length
 * @param replacementPool - Optional pool of replacement questions (same module)
 */
export function filterSession(
  questions: Question[],
  targetCount: number,
  replacementPool?: Question[],
): { questions: Question[]; stats: GuardStats } {
  const stats: GuardStats = {
    total: questions.length,
    passed: 0,
    softFailed: 0,
    fatalRejected: 0,
    replaced: 0,
    reasons: {},
    rejections: [],
  }

  const filtered: Question[] = []
  const usedIds = new Set(questions.map((q) => q.id))

  for (const q of questions) {
    const verdict = evaluateQuestion(q)

    if (verdict.ok) {
      filtered.push(q)
      stats.passed++
      continue
    }

    // Track reason
    const reason = verdict.reason ?? 'unknown'
    stats.reasons[reason] = (stats.reasons[reason] ?? 0) + 1

    if (verdict.severity === 'soft') {
      // Soft fail: allow through but log
      filtered.push(q)
      stats.softFailed++
      stats.rejections.push({
        questionId: q.id,
        familyId: q.familyId,
        reason,
        severity: 'soft',
      })
      debug(
        `[PedagogicalGuard] Soft fail: ${q.id} — ${reason} (allowed through)`,
      )
      continue
    }

    // Fatal: try to find replacement
    stats.fatalRejected++
    stats.rejections.push({
      questionId: q.id,
      familyId: q.familyId,
      reason,
      severity: 'fatal',
    })
    warn(`[PedagogicalGuard] Fatal reject: ${q.id} — ${reason}`)

    if (replacementPool) {
      const replacement = findReplacement(q, replacementPool, usedIds)
      if (replacement) {
        filtered.push(replacement)
        usedIds.add(replacement.id)
        stats.replaced++
        debug(`[PedagogicalGuard] Replaced ${q.id} with ${replacement.id}`)
      }
    }
  }

  // Trim to target count
  const result = filtered.slice(0, targetCount)

  return { questions: result, stats }
}

/**
 * Find a replacement question from the same family and similar tier.
 */
function findReplacement(
  rejected: Question,
  pool: Question[],
  usedIds: Set<string>,
): Question | null {
  // Prefer same family, same tier
  const sameFamilySameTier = pool.find(
    (q) =>
      q.familyId === rejected.familyId &&
      q.tier === rejected.tier &&
      !usedIds.has(q.id) &&
      evaluateQuestion(q).ok,
  )
  if (sameFamilySameTier) return sameFamilySameTier

  // Same family, adjacent tier (±1)
  const sameFamilyNearTier = pool.find(
    (q) =>
      q.familyId === rejected.familyId &&
      Math.abs(q.tier - rejected.tier) <= 1 &&
      !usedIds.has(q.id) &&
      evaluateQuestion(q).ok,
  )
  if (sameFamilyNearTier) return sameFamilyNearTier

  // Same module, same tier
  const sameModuleSameTier = pool.find(
    (q) =>
      q.module === rejected.module &&
      q.tier === rejected.tier &&
      !usedIds.has(q.id) &&
      evaluateQuestion(q).ok,
  )
  if (sameModuleSameTier) return sameModuleSameTier

  return null
}
